import { useSyncExternalStore } from 'react'
import { Modal, message } from 'antd'
import { callApi } from '@/lib/callApi'
import { releaseEndedOrder } from '@/lib/anchorOrderRelease'
import { clearCallOrderConnecting, isCallOrderEnded, markCallOrderConnecting, markCallOrderEnded } from '@/lib/callOrderLifecycleGuard'
import { clearIncomingCallTimeout, scheduleIncomingCallTimeout } from '@/lib/realtimeClient'
import { type LocalMediaFailureReason, shengwangClient } from '@/lib/shengwangClient'
import { reportAnchorActivity } from '@/lib/anchorActivityLogger'
import { rtcJoinMetadata } from '@/lib/rtcJoinDiagnostics'
import { canAnchorHandleIncomingCall } from '@/lib/anchorEligibility'
import { useCallStore } from '@/store/callStore'
import { useAuthStore } from '@/store/authStore'
import { usePresenceStore } from '@/store/presenceStore'
import type { VideoCall } from '@/types'

const DEVICE_PERMISSION_DECLINE_REASON = 'device_permission_denied'

type ModalHandle = { destroy: () => void }

let localMediaPermissionDialog: { orderID: string; destroy: () => void } | null = null
let localMediaDialogStoreSubscribed = false

const localMediaErrorMessages: Record<LocalMediaFailureReason, { title: string; message: string }> = {
  NO_MICROPHONE: {
    title: 'Microphone not found',
    message: 'Please connect or enable your microphone, then try again.',
  },
  NO_CAMERA: {
    title: 'Camera not found',
    message: 'Please connect or enable your camera, then try again.',
  },
  PERMISSION_DENIED: {
    title: 'Permission required',
    message: 'Please allow camera and microphone access to answer this call.',
  },
  DEVICE_NOT_FOUND: {
    title: 'Device not found',
    message: 'The selected camera or microphone is unavailable. Please check your device and try again.',
  },
  UNKNOWN: {
    title: 'Unable to access devices',
    message: 'Please check your camera, microphone, and browser permissions, then try again.',
  },
}

export function localMediaFailureMessage(reason: LocalMediaFailureReason) {
  const copy = localMediaErrorMessages[reason] || localMediaErrorMessages.UNKNOWN
  return copy.message
}

export function outgoingLocalMediaFailureMessage(reason: LocalMediaFailureReason) {
  if (reason === 'PERMISSION_DENIED') {
    return 'Please allow camera and microphone access to start this call.'
  }
  return localMediaFailureMessage(reason)
}

export async function ensureCallMediaSDKReady(orderID = '', options: { requireVisibleIncoming?: boolean } = {}) {
  if (!canAcceptVisibleIncomingOrder(orderID, options)) {
    return false
  }
  if (shengwangClient.isSDKReady()) {
    return true
  }
  if (shengwangClient.getSDKLoadState() === 'loading') {
    message.error('Call media is still loading. Please try again in a moment.')
    return false
  }
  try {
    await shengwangClient.preloadSDK()
  } catch {
    message.error('Unable to load call media. Please try again.')
    return false
  }
  if (!canAcceptVisibleIncomingOrder(orderID, options)) {
    return false
  }
  return shengwangClient.isSDKReady()
}

export function useIncomingCallActions() {
  ensureLocalMediaDialogStoreSubscription()
  const incomingCall = useCallStore((state) => state.incomingCall)
  const incomingActionOrderID = useCallStore((state) => state.incomingActionOrderID)
  const incomingAction = useCallStore((state) => state.incomingAction)
  const sdkLoadState = useSyncExternalStore(
    shengwangClient.subscribeSDKReady.bind(shengwangClient),
    () => shengwangClient.getSDKLoadState(),
    () => shengwangClient.getSDKLoadState()
  )
  const sdkReady = sdkLoadState === 'ready'
  const sdkLoading = sdkLoadState === 'idle' || sdkLoadState === 'loading'
  const pendingAction = incomingCall && incomingActionOrderID === incomingCall.order_id ? incomingAction : null

  const rejectCall = async () => {
    if (!incomingCall?.order_id || pendingAction || !claimIncomingAction(incomingCall.order_id, 'reject')) {
      return
    }
    await rejectIncomingCall(incomingCall, 'anchor_reject', { successMessage: 'Rejected' })
  }

  const acceptCall = async () => {
    if (!incomingCall?.order_id || pendingAction) {
      return
    }
    await autoAcceptIncomingCall(incomingCall)
  }

  return {
    incomingCall,
    pendingAction,
    sdkReady,
    sdkLoading,
    sdkLoadState,
    acceptCall,
    rejectCall,
  }
}

export async function autoAcceptIncomingCall(incomingCall: VideoCall) {
  const orderID = incomingCall.order_id
  if (!orderID || !canAcceptVisibleIncomingOrder(orderID, { requireVisibleIncoming: true }) || !claimIncomingAction(orderID, 'accept')) {
    return
  }
  if (!(await ensureCallMediaSDKReady(orderID, { requireVisibleIncoming: true }))) {
    releaseIncomingAction(orderID)
    return
  }
  if (!canAnchorHandleIncomingCall(useAuthStore.getState().anchor)) {
    clearIncomingCallTimeout(orderID)
    useCallStore.getState().setIncomingCall(null)
    useCallStore.getState().setIncomingAction(null, null)
    usePresenceStore.getState().setCallStatus('idle')
    releaseIncomingAction(orderID)
    message.error('Your account is unavailable for calls.')
    return
  }
  await runIncomingAccept(incomingCall)
}

export async function runIncomingAccept(incomingCall: VideoCall) {
  const orderID = incomingCall.order_id
  if (!orderID) {
    throw new Error('Missing order id')
  }
  if (!canAnchorHandleIncomingCall(useAuthStore.getState().anchor)) {
    clearIncomingCallTimeout(orderID)
    useCallStore.getState().setIncomingCall(null)
    useCallStore.getState().setIncomingAction(null, null)
    usePresenceStore.getState().setCallStatus('idle')
    message.error('Your account is unavailable for calls.')
    return
  }
  if (!(await ensureCallMediaSDKReady(orderID, { requireVisibleIncoming: true }))) {
    return
  }
  if (!canAcceptVisibleIncomingOrder(orderID, { requireVisibleIncoming: true })) {
    return
  }
  clearIncomingCallTimeout(orderID)
  try {
    const localMedia = await shengwangClient.prepareLocalTracksForAccept({ owner: `incoming:${orderID}` })
    if (!localMedia.ok) {
      await shengwangClient.leaveCall()
      if (isCallOrderEnded(orderID) || useCallStore.getState().incomingCall?.order_id !== orderID) {
        destroyLocalMediaPermissionDialog(orderID)
        return
      }
      scheduleIncomingCallTimeout(incomingCall)
      showLocalMediaPermissionDialog(incomingCall, localMedia.reason)
      return
    }
    if (isCallOrderEnded(orderID) || useCallStore.getState().incomingCall?.order_id !== orderID) {
      destroyLocalMediaPermissionDialog(orderID)
      await shengwangClient.leaveCall()
      return
    }

    await completeIncomingAccept(incomingCall)
  } catch (error) {
    if (hasCurrentCallForOrder(orderID)) {
      return
    }
    if (isAcceptedByOtherAnchorError(error)) {
      markCallOrderEnded(orderID)
      clearIncomingCallTimeout(orderID)
      destroyLocalMediaPermissionDialog(orderID)
      if (useCallStore.getState().incomingCall?.order_id === orderID) {
        useCallStore.getState().setIncomingCall(null)
      }
      useCallStore.getState().setIncomingAction(null, null)
      usePresenceStore.getState().setCallStatus('idle')
      message.info('This call was accepted by another anchor')
      return
    }
    if (useCallStore.getState().incomingCall?.order_id === orderID) {
      scheduleIncomingCallTimeout(incomingCall)
    }
    message.error(error instanceof Error ? error.message : 'Failed to accept')
  } finally {
    releaseIncomingAction(orderID)
  }
}

function isAcceptedByOtherAnchorError(error: unknown) {
  return error instanceof Error && /accepted by (?:another|other) anchor/i.test(error.message)
}

function canAcceptVisibleIncomingOrder(orderID = '', options: { requireVisibleIncoming?: boolean } = {}) {
  if (!orderID) {
    return true
  }
  if (isCallOrderEnded(orderID)) {
    return false
  }
  return !options.requireVisibleIncoming || useCallStore.getState().incomingCall?.order_id === orderID
}

export async function completeIncomingAccept(incomingCall: VideoCall) {
  const orderID = incomingCall.order_id
  if (!orderID) {
    throw new Error('Missing order id')
  }
  markCallOrderConnecting(orderID)
  const incomingAgoraUID = typeof incomingCall.agora_uid === 'number'
    ? incomingCall.agora_uid
    : incomingCall.uid
  const response = await callApi.acceptOrder(orderID, {
    channel_name: incomingCall.channel_name,
    ...(typeof incomingAgoraUID === 'number' ? { agora_uid: incomingAgoraUID } : null),
  }).catch(async (error) => {
    clearCallOrderConnecting(orderID)
    if (!hasCurrentCallForOrder(orderID)) {
      await shengwangClient.leaveCall()
    }
    throw error
  })
  if (isCallOrderEnded(orderID)) {
    clearCallOrderConnecting(orderID)
    await shengwangClient.leaveCall()
    return
  }
  const call = response.data
  if (!call) {
    clearCallOrderConnecting(orderID)
    await shengwangClient.leaveCall()
    throw new Error('Missing accept response')
  }
  const acceptedCall = mergeAcceptedIncomingCall(incomingCall, call)
  if (isCallOrderEnded(orderID)) {
    clearCallOrderConnecting(orderID)
    await shengwangClient.leaveCall()
    return
  }
  try {
    reportAnchorActivity({
      event_type: 'incoming_call_rtc_join_started',
      event_source: 'anchor_h5_rtc',
      order_id: orderID,
      call_direction: 'user_to_anchor',
      counterparty_user_id: acceptedCall.user_id,
      rtc_channel: acceptedCall.channel_name || acceptedCall.rtc_channel,
      rtc_uid: acceptedCall.uid,
      metadata: rtcJoinMetadata(acceptedCall, 'join_attempt'),
    })
    await shengwangClient.enterCall(acceptedCall)
    reportAnchorActivity({
      event_type: 'incoming_call_rtc_joined',
      event_source: 'anchor_h5_rtc',
      order_id: orderID,
      call_direction: 'user_to_anchor',
      counterparty_user_id: acceptedCall.user_id,
      rtc_channel: acceptedCall.channel_name || acceptedCall.rtc_channel,
      rtc_uid: acceptedCall.uid,
    })
  } catch (error) {
    clearCallOrderConnecting(orderID)
    reportAnchorActivity({
      event_type: 'incoming_call_rtc_join_failed',
      event_source: 'anchor_h5_rtc',
      order_id: orderID,
      call_direction: 'user_to_anchor',
      counterparty_user_id: acceptedCall.user_id,
      result: 'failed',
      reason_code: 'rtc_join_failed',
      error_code: 'rtc_join_failed',
      error_message: error instanceof Error ? error.message : 'Failed to accept',
      rtc_channel: acceptedCall.channel_name || acceptedCall.rtc_channel,
      rtc_uid: acceptedCall.uid,
      metadata: rtcJoinMetadata(acceptedCall, 'join_failed', error),
    })
    if (isCallOrderEnded(orderID)) {
      await shengwangClient.leaveCall()
      return
    }
    if (acceptedCall.order_id) {
      await releaseEndedOrder(acceptedCall.order_id, 'rtc_join_failed')
    } else {
      await callApi.endCall(orderID).catch(() => undefined)
    }
    useCallStore.getState().setCurrentCall(null)
    useCallStore.getState().setIncomingCall(null)
    usePresenceStore.getState().setCallStatus('idle')
    throw error
  }
  if (isCallOrderEnded(orderID)) {
    clearCallOrderConnecting(orderID)
    await shengwangClient.leaveCall()
    return
  }
  clearCallOrderConnecting(orderID)
  usePresenceStore.getState().setCallStatus('busy')
  useCallStore.getState().setCurrentCall(acceptedCall)
  useCallStore.getState().setIncomingCall(null)
  message.success('Accepted')
}

export function mergeAcceptedIncomingCall(incomingCall: VideoCall, call?: VideoCall | null): VideoCall {
  const acceptedChannel = call?.channel_name || call?.rtc_channel
  return call
    ? {
        ...incomingCall,
        ...call,
        order_id: incomingCall.order_id,
        channel_name: acceptedChannel,
        rtc_channel: acceptedChannel,
        app_id: call.app_id,
        uid: call.uid,
        rtc_token: call.rtc_token,
        expire_at: call.expire_at,
        direction: 'user_to_anchor',
        receiver_role: 'anchor',
        status: call.status,
      }
    : incomingCall
}

function claimIncomingAction(orderID: string, action: 'accept' | 'reject') {
  const calls = useCallStore.getState()
  if (calls.incomingActionOrderID) {
    return false
  }
  calls.setIncomingAction(orderID, action)
  return true
}

function releaseIncomingAction(orderID: string) {
  const calls = useCallStore.getState()
  if (calls.incomingActionOrderID === orderID) {
    calls.setIncomingAction(null, null)
  }
}

export function hasCurrentCallForOrder(orderID: string) {
  return useCallStore.getState().currentCall?.order_id === orderID
}

async function rejectIncomingCall(
  incomingCall: VideoCall,
  reasonCode: string,
  options: { successMessage?: string; localMediaFailureReason?: LocalMediaFailureReason } = {}
) {
  const orderID = incomingCall.order_id
  if (!orderID) {
    return
  }
  clearIncomingCallTimeout(orderID)
  const shouldReportRejectActivity = Boolean(options.localMediaFailureReason)
  try {
    destroyLocalMediaPermissionDialog(orderID)
    await callApi.rejectOrder(orderID, { reason_code: reasonCode })
    if (shouldReportRejectActivity) {
      reportIncomingRejectActivity(incomingCall, reasonCode, 'success', options.localMediaFailureReason)
    }
    if (useCallStore.getState().incomingCall?.order_id === orderID) {
      useCallStore.getState().setIncomingCall(null)
    }
    usePresenceStore.getState().setCallStatus('idle')
    if (options.successMessage) {
      message.success(options.successMessage)
    }
  } catch (error) {
    if (shouldReportRejectActivity) {
      reportIncomingRejectActivity(incomingCall, reasonCode, 'failed', options.localMediaFailureReason, error)
    }
    if (useCallStore.getState().incomingCall?.order_id === orderID) {
      scheduleIncomingCallTimeout(incomingCall)
    }
    message.error(error instanceof Error ? error.message : 'Failed to reject')
  } finally {
    releaseIncomingAction(orderID)
  }
}

function showLocalMediaPermissionDialog(incomingCall: VideoCall, reason: LocalMediaFailureReason) {
  ensureLocalMediaDialogStoreSubscription()
  const orderID = incomingCall.order_id
  if (!orderID || isCallOrderEnded(orderID) || useCallStore.getState().incomingCall?.order_id !== orderID) {
    return
  }
  const copy = localMediaErrorMessages[reason] || localMediaErrorMessages.UNKNOWN
  destroyLocalMediaPermissionDialog()
  const modal = Modal.confirm({
    title: copy.title,
    content: copy.message,
    okText: 'Try Again',
    cancelText: 'Decline',
    centered: true,
    maskClosable: false,
    onOk: async () => {
      if (useCallStore.getState().incomingCall?.order_id !== orderID) {
        destroyLocalMediaPermissionDialog(orderID)
        return
      }
      if (!claimIncomingAction(orderID, 'accept')) {
        return
      }
      if (!(await ensureCallMediaSDKReady(orderID, { requireVisibleIncoming: true }))) {
        releaseIncomingAction(orderID)
        return
      }
      destroyLocalMediaPermissionDialog(orderID)
      void runIncomingAccept(incomingCall)
    },
    onCancel: () => {
      if (useCallStore.getState().incomingCall?.order_id !== orderID) {
        destroyLocalMediaPermissionDialog(orderID)
        return
      }
      if (!claimIncomingAction(orderID, 'reject')) {
        return
      }
      destroyLocalMediaPermissionDialog(orderID)
      void rejectIncomingCall(incomingCall, DEVICE_PERMISSION_DECLINE_REASON, {
        localMediaFailureReason: reason,
      })
    },
  })
  localMediaPermissionDialog = {
    orderID,
    destroy: () => (modal as ModalHandle).destroy(),
  }
}

function reportIncomingRejectActivity(
  incomingCall: VideoCall,
  reasonCode: string,
  result: 'success' | 'failed',
  localMediaFailureReason?: LocalMediaFailureReason,
  error?: unknown
) {
  const orderID = incomingCall.order_id
  if (!orderID) {
    return
  }
  reportAnchorActivity({
    event_type: result === 'failed' ? 'incoming_call_reject_failed' : 'incoming_call_rejected',
    event_source: 'anchor_h5_ui',
    order_id: orderID,
    call_direction: 'user_to_anchor',
    counterparty_user_id: incomingCall.user_id,
    result,
    reason_code: reasonCode,
    error_code: result === 'failed' ? 'incoming_call_reject_failed' : undefined,
    error_message: result === 'failed' ? errorMessage(error) : undefined,
    metadata: {
      local_media_failure_reason: localMediaFailureReason,
      device_permission_decline: reasonCode === DEVICE_PERMISSION_DECLINE_REASON,
    },
  })
}

function ensureLocalMediaDialogStoreSubscription() {
  if (localMediaDialogStoreSubscribed) {
    return
  }
  localMediaDialogStoreSubscribed = true
  useCallStore.subscribe((state) => {
    const dialogOrderID = localMediaPermissionDialog?.orderID
    if (dialogOrderID && state.incomingCall?.order_id !== dialogOrderID) {
      destroyLocalMediaPermissionDialog(dialogOrderID)
    }
  })
}

function destroyLocalMediaPermissionDialog(orderID?: string) {
  const dialog = localMediaPermissionDialog
  if (!dialog || (orderID && dialog.orderID !== orderID)) {
    return
  }
  localMediaPermissionDialog = null
  dialog.destroy()
}

function errorMessage(error: unknown) {
  if (error instanceof Error) {
    return error.message
  }
  return String(error)
}
